import { ArrowLeft, ExternalLink, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { Textarea } from '@/components/ui/textarea'
import { errorMessage, isApiError } from '@/lib/api/errors'
import { formatDate } from '@/lib/format'
import {
  type HelpArticle,
  type HelpArticleInput,
  type HelpKind,
  type HelpStatus,
  useDeleteHelpArticle,
  useHelpArticle,
  useHelpArticles,
  useHelpPreview,
  useSaveHelpArticle,
} from './api'

const blank: HelpArticleInput = {
  title: '',
  kind: 'guide',
  help_category_id: null,
  summary: null,
  body: '',
  slug: null,
  status: 'draft',
}

/** Write or edit a help article: /help/new or /help/:articleId. */
export function ArticleEditorPage() {
  const { articleId } = useParams()
  const article = useHelpArticle(articleId)

  if (articleId !== undefined && article.isPending) return <Skeleton className="h-96" />
  if (article.isError) return <ErrorState error={article.error} onRetry={() => void article.refetch()} />

  // Mounted per article, so the form starts from what's saved.
  return <Editor key={article.data?.id ?? 'new'} article={article.data ?? null} />
}

function Editor({ article }: { article: HelpArticle | null }) {
  const navigate = useNavigate()
  // "Write" on a search that found nothing (Help center) starts the article with that search.
  const [params] = useSearchParams()
  const save = useSaveHelpArticle(article?.id)
  const remove = useDeleteHelpArticle()
  const list = useHelpArticles({})
  const categories = list.data?.meta.categories ?? []
  const [values, setValues] = useState<HelpArticleInput>(
    article
      ? {
          title: article.title,
          kind: article.kind,
          help_category_id: article.category?.id ?? null,
          summary: article.summary,
          body: article.body,
          slug: article.slug,
          status: article.status,
        }
      : { ...blank, title: params.get('title') ?? '' },
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [tab, setTab] = useState<'write' | 'preview'>('write')
  const [confirming, setConfirming] = useState(false)
  // The body can't change while the preview shows, so it's fetched once per look.
  const preview = useHelpPreview(values.body, tab === 'preview')
  const isFaq = values.kind === 'faq'
  const set = <K extends keyof HelpArticleInput>(key: K, value: HelpArticleInput[K]) =>
    setValues((v) => ({ ...v, [key]: value }))

  const submit = (status: HelpStatus) => {
    setErrors({})
    save.mutate(
      {
        ...values,
        status,
        summary: isFaq ? null : values.summary?.trim() || null,
        slug: values.slug?.trim() || null,
      },
      {
        onSuccess: (saved) => {
          toast.success(
            status === 'published'
              ? article?.status === 'published'
                ? 'Saved. The help center shows it now.'
                : 'Published on the help center.'
              : article?.status === 'published'
                ? 'Unpublished. It’s a draft again.'
                : 'Draft saved.',
          )
          setValues((v) => ({ ...v, status: saved.status, slug: saved.slug }))
          if (!article) void navigate(`/help/${saved.id}`, { replace: true })
        },
        onError: (err) => {
          if (isApiError(err) && err.isValidation) {
            setErrors(Object.fromEntries(Object.entries(err.errors).map(([k, v]) => [k, v[0] ?? ''])))
          } else toast.error(errorMessage(err))
        },
      },
    )
  }

  const published = article?.status === 'published'

  return (
    <>
      <Link
        to="/help"
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-ink-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Help center
      </Link>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="grid gap-1">
          <h1 className="display text-2xl text-ink">{article ? article.title : 'New article'}</h1>
          {article && (
            <p className="flex flex-wrap items-center gap-2 text-[13px] text-ink-muted">
              <Badge tone={published ? 'success' : 'outline'}>{published ? 'Published' : 'Draft'}</Badge>
              {article.updated_at && `Updated ${formatDate(article.updated_at)}`}
              {article.updated_by && ` by ${article.updated_by}`}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {published && (
            <Button variant="secondary" asChild>
              <a href={article.url} target="_blank" rel="noreferrer">
                <ExternalLink /> View
              </a>
            </Button>
          )}
          {article && (
            <Button variant="ghost" onClick={() => setConfirming(true)}>
              <Trash2 /> Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <Card className="grid gap-5 p-5">
          <Segmented<HelpKind>
            label="Type"
            value={values.kind}
            onChange={(kind) => set('kind', kind)}
            options={[
              { value: 'guide', label: 'Guide' },
              { value: 'troubleshooting', label: 'Troubleshooting' },
              { value: 'faq', label: 'FAQ' },
            ]}
          />
          <Field label={isFaq ? 'Question' : 'Title'} htmlFor="help-title" required error={errors.title}>
            <Input
              id="help-title"
              value={values.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder={isFaq ? 'How do I…?' : 'Connect Google Search Console'}
            />
          </Field>
          {!isFaq && (
            <Field
              label="Summary"
              htmlFor="help-summary"
              error={errors.summary}
              hint="One sentence for the article list, search results and Google."
            >
              <Input
                id="help-summary"
                value={values.summary ?? ''}
                onChange={(e) => set('summary', e.target.value)}
              />
            </Field>
          )}
          <div className="grid gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <label
                htmlFor="help-body"
                className="text-[13px] font-medium text-ink after:ml-0.5 after:text-danger after:content-['*']"
              >
                {isFaq ? 'Answer' : 'Article'}
              </label>
              <Segmented<'write' | 'preview'>
                label="Editor view"
                value={tab}
                onChange={setTab}
                options={[
                  { value: 'write', label: 'Write' },
                  { value: 'preview', label: 'Preview' },
                ]}
              />
            </div>
            {tab === 'write' ? (
              <Textarea
                id="help-body"
                className="min-h-[24rem] font-mono text-[13px] leading-relaxed"
                value={values.body}
                onChange={(e) => set('body', e.target.value)}
                aria-invalid={errors.body ? true : undefined}
              />
            ) : (
              <div
                className="help-preview min-h-[24rem] rounded-md border border-border bg-surface-raised px-5 py-4 text-sm leading-relaxed text-ink"
                // Rendered by the API with raw HTML stripped, exactly as the help center shows it.
                dangerouslySetInnerHTML={{ __html: preview.data ?? '' }}
              />
            )}
            {errors.body ? (
              <p role="alert" className="text-xs text-danger">
                {errors.body}
              </p>
            ) : (
              <p className="text-xs text-ink-subtle">
                Markdown: <code>## Heading</code>, <code>**bold**</code>, <code>- list item</code>,{' '}
                <code>1. step</code>, <code>[link text](https://…)</code>, <code>`code`</code>. Link to
                another article with <code>[title](/articles/its-address)</code>.
              </p>
            )}
          </div>
        </Card>

        <div className="grid h-fit gap-4">
          <Card className="grid gap-4 p-5">
            <Field label="Section" htmlFor="help-category" error={errors.help_category_id}>
              <NativeSelect
                id="help-category"
                value={values.help_category_id ?? ''}
                onChange={(e) => set('help_category_id', e.target.value || null)}
              >
                <option value="">No section</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field
              label="Address"
              htmlFor="help-slug"
              error={errors.slug}
              hint={
                article
                  ? `support.arvaseo.com/articles/${values.slug ?? ''}`
                  : 'Made from the title if left empty.'
              }
            >
              <Input
                id="help-slug"
                value={values.slug ?? ''}
                onChange={(e) => set('slug', e.target.value)}
                placeholder="made-from-the-title"
              />
            </Field>
          </Card>
          <Card className="grid gap-2 p-5">
            <Button
              onClick={() => submit('published')}
              loading={save.isPending && save.variables.status === 'published'}
            >
              {published ? 'Save changes' : 'Publish'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => submit('draft')}
              loading={save.isPending && save.variables.status === 'draft'}
            >
              {published ? 'Unpublish' : 'Save draft'}
            </Button>
            <p className="text-xs text-ink-subtle">
              {published ? 'Changes are live as soon as you save.' : 'Drafts are only visible here.'}
            </p>
          </Card>
        </div>
      </div>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent
          title="Delete this article?"
          description="It disappears from the help center, and links to it stop working. To keep it but hide it, unpublish it instead."
          footer={
            <>
              <DialogClose asChild>
                <Button variant="ghost">Cancel</Button>
              </DialogClose>
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() =>
                  article &&
                  remove.mutate(article.id, {
                    onSuccess: () => {
                      toast.success('Article deleted.')
                      void navigate('/help', { replace: true })
                    },
                    onError: (err) => toast.error(errorMessage(err)),
                  })
                }
              >
                Delete
              </Button>
            </>
          }
        />
      </Dialog>
    </>
  )
}
